import { useGetAllOfficesQuery } from '@/app/features/control-panel/organization/organization.api';
import type { GetAllOfficesParams } from '@/app/features/control-panel/organization/organization.type';
import { ChevronDownIcon } from '@/icons';
import { Select, Tooltip, type SelectProps } from 'antd';
import { useMemo, useState } from 'react';

type OfficeOption = { value: string; label: string };

type OfficeSelectProps = Omit<
  SelectProps<string | string[]>,
  'options' | 'mode' | 'loading' | 'value' | 'onChange'
> & {
  mode?: 'multiple';
  /** An office id, or ids in multiple mode. */
  value?: string | string[];
  // Typed loosely because some forms switch `mode` at runtime (create vs edit).
  onChange?: (value: any, option?: unknown) => void;
  /** `ACTIVE` (default) lists active offices only; `ALL` includes inactive ones. */
  officeStatus?: 'ACTIVE' | 'ALL';
  /** Show "Head Office" / "Branch Office" groups (default true). */
  groupByType?: boolean;
  /** "Select all" / "Clear" in the dropdown header (multiple mode, default true). */
  allowSelectAll?: boolean;
  /** Labels for selected offices that are not in the list (e.g. now inactive). */
  fallbackOptions?: OfficeOption[];
};

// Same args everywhere so every OfficeSelect shares one cached request.
const ACTIVE_ARGS: { params: GetAllOfficesParams } = {
  params: { take: 0, status: 'ACTIVE' },
};
const ALL_ARGS: { params: GetAllOfficesParams } = { params: { take: 0 } };

const matches = (label: string, search: string) =>
  label.toLowerCase().includes(search.trim().toLowerCase());

/**
 * Office picker that loads every office once (take: 0) and searches in the
 * browser. Offices per company are bounded, so this stays fast; the virtual
 * list keeps hundreds of options smooth.
 */
export default function OfficeSelect({
  mode,
  officeStatus = 'ACTIVE',
  groupByType = true,
  allowSelectAll = true,
  fallbackOptions,
  value,
  onChange,
  placeholder = 'Select office',
  ...rest
}: OfficeSelectProps) {
  const [search, setSearch] = useState('');
  const isMultiple = mode === 'multiple';

  const { data, isLoading } = useGetAllOfficesQuery(
    officeStatus === 'ALL' ? ALL_ARGS : ACTIVE_ARGS,
  );

  const { flatOptions, groupedOptions } = useMemo(() => {
    const offices = data?.data?.data ?? [];
    const flat: OfficeOption[] = offices.map((office) => ({
      value: office.id,
      label: office.name,
    }));

    const known = new Set(flat.map((option) => option.value));
    const missing = (fallbackOptions ?? []).filter(
      (option) => !known.has(option.value),
    );

    const head = offices
      .filter((office) => office.type === 'HEAD')
      .map((office) => ({ value: office.id, label: office.name }));
    const branch = offices
      .filter((office) => office.type !== 'HEAD')
      .map((office) => ({ value: office.id, label: office.name }));

    const grouped =
      groupByType && head.length && branch.length
        ? [
            { label: 'Head Office', options: head },
            { label: 'Branch Office', options: branch },
            ...(missing.length ? [{ label: 'Other', options: missing }] : []),
          ]
        : [...flat, ...missing];

    return { flatOptions: [...flat, ...missing], groupedOptions: grouped };
  }, [data, fallbackOptions, groupByType]);

  const selected = useMemo(
    () => (Array.isArray(value) ? value : value ? [value] : []),
    [value],
  );

  const labelOf = (id: string) =>
    flatOptions.find((option) => option.value === id)?.label ?? id;

  const emit = (next: string[]) => {
    onChange?.(
      next,
      flatOptions.filter((option) => next.includes(option.value)),
    );
  };

  // "Select all" / "Clear" act on the searched offices when a search is typed.
  const visibleIds = flatOptions
    .filter((option) => matches(option.label, search))
    .map((option) => option.value);

  const handleSelectAll = () =>
    emit(Array.from(new Set([...selected, ...visibleIds])));

  const handleClear = () =>
    emit(search ? selected.filter((id) => !visibleIds.includes(id)) : []);

  const popupRender: SelectProps['popupRender'] =
    isMultiple && allowSelectAll
      ? (menu) => (
          <>
            <div className="flex items-center justify-between px-3 py-2 text-sm border-b border-theme-gray-700">
              <span className="text-white/50">
                {selected.length} / {flatOptions.length} selected
              </span>
              <span className="flex gap-4">
                <button
                  type="button"
                  className="text-theme-primary disabled:opacity-50"
                  disabled={!visibleIds.length}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleSelectAll}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="text-white/70 hover:text-white disabled:opacity-50"
                  disabled={!selected.length}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleClear}
                >
                  Clear
                </button>
              </span>
            </div>
            {menu}
          </>
        )
      : undefined;

  return (
    <Select
      showSearch
      optionFilterProp="label"
      placeholder={placeholder}
      suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
      {...rest}
      mode={mode}
      value={value}
      onChange={(next, option) => onChange?.(next, option)}
      loading={isLoading}
      options={groupedOptions as SelectProps['options']}
      searchValue={isMultiple ? search : undefined}
      onSearch={isMultiple ? setSearch : rest.onSearch}
      onOpenChange={(open) => {
        if (!open) setSearch('');
        rest.onOpenChange?.(open);
      }}
      maxTagCount={isMultiple ? 'responsive' : undefined}
      maxTagPlaceholder={(omitted) => (
        <Tooltip
          title={omitted.map((option) => labelOf(String(option.value))).join(', ')}
        >
          <span>+{omitted.length}</span>
        </Tooltip>
      )}
      popupRender={popupRender}
    />
  );
}
